import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Truck, DollarSign, Send, Wrench, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONES = {
  draft: "bg-slate-500/20 text-slate-300 border-slate-500/40",
  sent_to_tech: "bg-blue-500/20 text-blue-300 border-blue-500/40",
  tech_completed: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  sent_to_finance: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  paid: "bg-emerald-600/30 text-emerald-200 border-emerald-500/40",
  closed: "bg-slate-700/40 text-slate-300 border-slate-600",
};

const STATUS_LABELS = {
  draft: "Rascunho",
  sent_to_tech: "Com Técnico",
  tech_completed: "Técnico OK",
  sent_to_finance: "No Financeiro",
  paid: "Pago",
  closed: "Fechado",
};

export default function FullTimeOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [showFinancial, setShowFinancial] = useState(false);
  const [financial, setFinancial] = useState(null);

  const load = () => {
    setLoading(true);
    const q = filter ? `?status=${filter}` : "";
    api.get(`/fulltime-orders${q}`).then((r) => setOrders(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  useEffect(() => {
    if (showFinancial) {
      api.get("/fulltime-orders/financial/summary").then((r) => setFinancial(r.data));
    }
  }, [showFinancial]);

  const remove = async (o, evt) => {
    evt?.preventDefault();
    evt?.stopPropagation();
    if (!window.confirm(`Excluir Ordem ${o.order_number}?\n\nWork Order e Invoice vinculados serão arquivados.`)) return;
    try {
      await api.delete(`/fulltime-orders/${o.id}`);
      toast.success("Ordem excluída");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    }
  };

  const filtered = orders.filter((o) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (o.order_number || "").toLowerCase().includes(s)
      || (o.customer_name || "").toLowerCase().includes(s)
      || (o.vin || "").toLowerCase().includes(s)
      || (o.fulltime_reference || "").toLowerCase().includes(s);
  });

  const statuses = ["", "draft", "sent_to_tech", "tech_completed", "sent_to_finance", "paid", "closed"];

  return (
    <div className="space-y-6 animate-slide-up" data-testid="fulltime-orders-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-2 flex items-center gap-2">
            <Truck className="w-3.5 h-3.5" /> Ordens FullTime
          </div>
          <h1 className="font-display text-4xl font-semibold tracking-tight">Ordens FullTime</h1>
          <p className="text-sm text-slate-400 mt-1.5">Serviços B2B da FullTime → TrueLine. Financeiro separado, sem vendedor, sem comissão.</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className={showFinancial ? "border-amber-500/40 text-amber-300 bg-amber-500/10" : "border-slate-700"}
            onClick={() => setShowFinancial(!showFinancial)}
            data-testid="toggle-financial-btn"
          >
            <DollarSign className="w-4 h-4 mr-2" />
            Financeiro FullTime
          </Button>
          <Link to="/fulltime/new">
            <Button className="bg-emerald-600 hover:bg-emerald-500" data-testid="new-fulltime-order-btn">
              <Plus className="w-4 h-4 mr-2" /> Nova Ordem
            </Button>
          </Link>
        </div>
      </div>

      {showFinancial && financial && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-5" data-testid="fulltime-financial-panel">
          <div className="grid md:grid-cols-3 gap-3 mb-4">
            <div className="rounded-lg border border-slate-800 bg-[#0B1120] p-4">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Total Faturado</div>
              <div className="font-display text-2xl font-semibold text-slate-200 mt-1">{financial.count} invoices</div>
            </div>
            <div className="rounded-lg border border-slate-800 bg-[#0B1120] p-4">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-400">Recebido</div>
              <div className="font-display text-2xl font-semibold text-emerald-300 mt-1 font-mono">${financial.paid.toFixed(2)}</div>
            </div>
            <div className="rounded-lg border border-slate-800 bg-[#0B1120] p-4">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-400">Pendente</div>
              <div className="font-display text-2xl font-semibold text-amber-300 mt-1 font-mono">${financial.pending.toFixed(2)}</div>
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Invoices FullTime (isoladas do Billing principal)</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-800 text-slate-500 uppercase tracking-wider">
                  <th className="text-left py-2 pr-3">Nº</th>
                  <th className="text-left py-2 pr-3">Cliente</th>
                  <th className="text-left py-2 pr-3">Veículo</th>
                  <th className="text-left py-2 pr-3">Status</th>
                  <th className="text-right py-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {financial.invoices.slice(0, 20).map((i) => (
                  <tr key={i.id} className="border-b border-slate-800/40" data-testid={`ft-invoice-${i.id}`}>
                    <td className="py-2 pr-3 font-mono text-blue-300">{i.invoice_number}</td>
                    <td className="py-2 pr-3 text-slate-200">{i.recipient_name}</td>
                    <td className="py-2 pr-3 text-slate-400">{i.vehicle_year} {i.vehicle_make} {i.vehicle_model}</td>
                    <td className="py-2 pr-3"><Badge className={`${i.status === "paid" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "bg-amber-500/15 text-amber-300 border-amber-500/40"} border text-[10px]`}>{i.status}</Badge></td>
                    <td className="py-2 text-right font-mono text-emerald-300">${(i.total || 0).toFixed(2)}</td>
                  </tr>
                ))}
                {financial.invoices.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-6 text-slate-500 italic">Nenhuma invoice FullTime ainda</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            data-testid="ft-search-input"
            placeholder="Buscar Order #, VIN, cliente ou ref FullTime..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-[#0B1120] border-slate-800"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto">
          {statuses.map((s) => (
            <button
              key={s || "all"}
              data-testid={`ft-filter-${s || "all"}`}
              onClick={() => setFilter(s)}
              className={`px-3 py-2 text-xs uppercase tracking-wide font-bold rounded-lg border transition-colors whitespace-nowrap ${
                filter === s ? "bg-blue-600/20 text-blue-300 border-blue-500/40" : "bg-slate-900/40 text-slate-400 border-slate-800 hover:border-slate-600"
              }`}
            >
              {s ? STATUS_LABELS[s] || s : "Todas"}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-12 text-center text-slate-500">
          <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" /> Carregando...
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-16 text-center">
          <Truck className="w-10 h-10 text-slate-700 mx-auto mb-3" />
          <div className="text-slate-400 text-sm">Nenhuma ordem FullTime ainda.</div>
          <Link to="/fulltime/new"><Button variant="outline" className="mt-4 border-slate-700">Criar primeira ordem</Button></Link>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-900/40">
                <th className="text-left text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Nº</th>
                <th className="text-left text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">VIN</th>
                <th className="text-left text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Veículo</th>
                <th className="text-left text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Cliente</th>
                <th className="text-left text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Ref FT</th>
                <th className="text-left text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Status</th>
                <th className="text-right text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Total</th>
                <th className="text-right text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <tr key={o.id} className="border-b border-slate-800/60 hover:bg-slate-800/20" data-testid={`ft-order-row-${o.id}`}>
                  <td className="px-5 py-3.5 font-mono text-xs text-blue-300">
                    <Link to={`/fulltime/${o.id}`} className="hover:underline">{o.order_number}</Link>
                  </td>
                  <td className="px-5 py-3.5 font-mono text-xs text-slate-300">{o.vin || "—"}</td>
                  <td className="px-5 py-3.5 text-sm">{o.vehicle_year} {o.vehicle_make} {o.vehicle_model}</td>
                  <td className="px-5 py-3.5 text-sm text-slate-300">{o.customer_name}</td>
                  <td className="px-5 py-3.5 text-xs font-mono text-slate-500">{o.fulltime_reference || "—"}</td>
                  <td className="px-5 py-3.5">
                    <Badge className={`${STATUS_TONES[o.status] || STATUS_TONES.draft} border text-[10px] uppercase tracking-wider`}>
                      {STATUS_LABELS[o.status] || o.status}
                    </Badge>
                  </td>
                  <td className="px-5 py-3.5 text-right font-mono text-emerald-400">${(o.total || 0).toFixed(2)}</td>
                  <td className="px-5 py-3.5 text-right">
                    <button
                      onClick={(e) => remove(o, e)}
                      data-testid={`ft-delete-${o.id}`}
                      className="p-1.5 rounded hover:bg-rose-500/10 text-slate-500 hover:text-rose-300"
                      title="Excluir"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
